import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';
import { z } from 'zod';

import {
  SUBSCRIPTION_PAYMENT_METHOD_LABEL,
  type SubscriptionPaymentMethod,
} from '@/constants/institute';
import { instituteToday } from '@/features/teacher/utils';

/**
 * Recording a licence payment.
 *
 * Mirrors `backend/src/modules/platform/subscriptions/subscriptions.validation.ts`,
 * which is the authority — this copy is for fast feedback only (CLAUDE.md §6).
 */

const METHODS = Object.keys(SUBSCRIPTION_PAYMENT_METHOD_LABEL) as [
  SubscriptionPaymentMethod,
  ...SubscriptionPaymentMethod[],
];

/**
 * The licence year that starts in `start` — 2026 → `2026-27`.
 */
function licenceYearStarting(start: number): string {
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}

/**
 * The licence year a day falls in, as invoiced — April to March, the way an
 * Indian school's year and its accounts both run. `2026-10-10` → `2026-27`.
 *
 * Takes the `YYYY-MM-DD` day rather than a `Date`, so "today" is the Indian
 * day from `instituteToday()` and not the browser's: on the night of 31 March
 * a laptop set to another zone would otherwise file the payment under the
 * wrong year.
 */
export function licenceYearOf(day: string): string {
  const year = Number(day.slice(0, 4));
  const month = Number(day.slice(5, 7));
  return licenceYearStarting(month >= 4 ? year : year - 1);
}

/**
 * The renewal date a licence year suggests: 1 April after it ends.
 * `2026-27` → `2027-04-01`. Only a default — a licence sold mid-year renews
 * mid-year, which is why the form lets it be changed.
 */
export function defaultRenewalFor(periodYear: string): string {
  const start = Number(periodYear.slice(0, 4));
  return Number.isFinite(start) ? `${start + 1}-04-01` : '';
}

/** The year now, the four before it and the one after — what a payment can be for. */
export function licenceYearOptions(today = instituteToday()) {
  const current = Number(licenceYearOf(today).slice(0, 4));
  return Array.from({ length: 6 }, (_, index) => {
    const value = licenceYearStarting(current + 1 - index);
    return { value, label: value };
  });
}

const optionalText = (max: number, tooLong: string) =>
  z
    .string()
    .trim()
    .max(max, tooLong)
    .transform((value) => value || null);

export const subscriptionPaymentSchema = z.object({
  periodYear: z.string().regex(/^\d{4}-\d{2}$/, 'Choose the year this pays for'),
  amount: z
    .string()
    .trim()
    .min(1, 'Enter the amount received')
    .regex(/^\d+(\.\d{1,2})?$/, 'Rupees, with at most two decimal places')
    .refine((value) => Number(value) > 0, 'A payment must be more than ₹0')
    .refine((value) => Number(value) <= 9_999_999_999.99, 'That looks too high — check the amount'),
  paidOn: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Pick the day it was paid')
    .refine((value) => value <= instituteToday(), 'The payment date cannot be in the future'),
  renewsOn: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, 'Pick the renewal date'),
  method: z.enum(METHODS, { errorMap: () => ({ message: 'Choose how the money was paid' }) }),
  referenceNo: optionalText(100, 'That reference is too long'),
  note: optionalText(255, 'Keep the note under 255 characters'),
})
  .refine((input) => input.renewsOn > input.paidOn, {
    path: ['renewsOn'],
    message: 'The renewal date must be after the payment date',
  });

export type SubscriptionPaymentPayload = z.infer<typeof subscriptionPaymentSchema>;

export interface SubscriptionPaymentFormValues {
  periodYear: string;
  amount: string;
  paidOn: string;
  renewsOn: string;
  method: SubscriptionPaymentMethod;
  referenceNo: string;
  note: string;
}

export const subscriptionPaymentResolver: Resolver<SubscriptionPaymentFormValues> = zodResolver(
  subscriptionPaymentSchema,
  undefined,
  { raw: true },
);
